import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { GeoLocationStatus } from '@endeavour/enhanced-geo-integration';

import { config } from '../../../config/config';
import { useGeoLocationHeartbeat } from '../../geolocation/hooks/use-geolocation-heartbeat';
import { useGeolocationStatus } from '../../geolocation/util/geolocation-context';
import { useAppDispatch } from '../../main/store/root.store';
import { useDesktop } from '../../shared/hooks/breakpoint';
import { useDemographicsFilter } from '../../shared/hooks/demographic';
import { useCurrentCurrency } from '../../shared/hooks/use-current-currency';
import { selectLanguage } from '../../shared/store/locale.store';
import { gameplayStore, getLaunchUrls, selectGames } from '../store/gameplay.store';

import { useGameData } from './use-game-data';

export function useGameLaunch({
    initialSlugs,
    onSlugsChange,
}: {
    initialSlugs: Array<string | undefined>;
    onSlugsChange?: (slugs: Array<string | undefined>) => void;
}) {
    const { slots, updateSlotCount } = useGameData({
        initialSlugs,
        onSlugsChange,
    });
    const { status, errors } = useGeolocationStatus();
    const { sendHeartbeat } = useGeoLocationHeartbeat();
    const hasLocationError =
        status !== GeoLocationStatus.ALLOWED &&
        config.GEOLOCATION.ENHANCED_GEOLOCATION_REQUIRED.GAMEPLAY;
    const isLoadingLocation =
        status === GeoLocationStatus.PENDING &&
        config.GEOLOCATION.ENHANCED_GEOLOCATION_REQUIRED.GAMEPLAY;
    const locationErrorCode = errors[0] ?? (isLoadingLocation ? 'pending-location' : undefined);
    const language = useSelector(selectLanguage);
    const isDesktop = useDesktop();
    const games = useSelector(selectGames);
    const [params] = useSearchParams();
    const auth = useAuth();
    const filteredGames = useDemographicsFilter(games ?? []);
    const dispatch = useAppDispatch();
    const realmoney = params.get('realmoney');
    const currentCurrency = useCurrentCurrency();
    const isAuthenticated = auth.isAuthenticated;
    const userProfileId = auth.user?.profile.user_profile_id as string;

    useEffect(() => {
        if (hasLocationError) {
            dispatch(
                gameplayStore.actions.disableGamesWithError({
                    errorCode: locationErrorCode,
                }),
            );
            return;
        }

        dispatch(
            gameplayStore.actions.enableGamesWithError({
                errorCode: locationErrorCode,
            }),
        );
    }, [hasLocationError, locationErrorCode, slots, dispatch]);

    useEffect(() => {
        const launch = async () => {
            const gamesRequiringLoad = filteredGames.filter((game) => {
                const slot = slots.find(({ slug }) => slug === game.slug);

                if (!slot) {
                    return false;
                }

                const isSameCurrency = slot.currency === currentCurrency;
                const shouldSlotLoad =
                    (!slot.launchUrl && isSameCurrency && !slot.hasError) || !isSameCurrency;
                return slot.game && !slot.isLoadingLaunchUrl && shouldSlotLoad;
            });

            if (gamesRequiringLoad.length === 0) {
                return;
            }

            await dispatch(
                getLaunchUrls({
                    games: gamesRequiringLoad.map((game) => {
                        return {
                            internalGameId: game.id,
                        };
                    }),
                    isDemoPlay: realmoney === null ? !isAuthenticated : Boolean(realmoney),
                    isMobile: !isDesktop,
                    language,
                    currencyCode: currentCurrency,
                    origin: window.location.origin,
                    userProfileId,
                    // TODO: Send real geo localized country code
                    countryCode: 'FI',
                }),
            );

            sendHeartbeat();
        };

        launch();
    }, [
        filteredGames,
        slots,
        isAuthenticated,
        userProfileId,
        currentCurrency,
        isDesktop,
        language,
        realmoney,
        sendHeartbeat,
        dispatch,
    ]);

    return {
        slots,
        updateSlotCount,
    };
}
